import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'

interface CheckHistory {
  timestamp: string
  detected: boolean
  times_count: number
}

interface TimelineChartProps {
  data: CheckHistory[]
}

export default function TimelineChart({ data }: TimelineChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data
      .slice()
      .reverse() // Most recent first -> oldest first for timeline
      .map((check, index) => ({
        index,
        time: format(parseISO(check.timestamp), 'HH:mm', { locale: es }),
        fullTime: format(parseISO(check.timestamp), 'PPp', { locale: es }),
        detected: check.detected ? 1 : 0,
        timesCount: check.times_count || 0,
        status: check.detected ? 'Cita Encontrada' : 'Sin Citas'
      }))
  }, [data])

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No hay datos de historial disponibles</p>
      </div>
    )
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-md">
          <p className="font-semibold text-gray-900">{data.fullTime}</p>
          <p className={`text-sm mt-1 ${data.detected ? 'text-green-600' : 'text-gray-600'}`}>
            {data.status}
          </p>
          {data.timesCount > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Horarios detectados: {data.timesCount}
            </p>
          )}
        </div>
      )
    }
    return null
  }

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="time"
            stroke="#6b7280"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#6b7280"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            domain={[0, 1]}
            ticks={[0, 1]}
            tickFormatter={(value) => (value === 1 ? 'Encontrado' : 'Sin Citas')}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value) => {
              if (value === 'detected') return 'Detecciones'
              if (value === 'timesCount') return 'Horarios Disponibles'
              return value
            }}
          />
          <ReferenceLine y={0.5} stroke="#d1d5db" strokeDasharray="5 5" />
          <Line
            type="stepAfter"
            dataKey="detected"
            stroke="#10b981"
            strokeWidth={2}
            dot={(props) => {
              const { cx, cy, payload } = props
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={payload.detected ? 5 : 3}
                  fill={payload.detected ? '#10b981' : '#9ca3af'}
                  stroke="white"
                  strokeWidth={2}
                />
              )
            }}
            activeDot={{ r: 7 }}
          />
          {/* Only render timesCount line if there's data */}
          {chartData.some(d => d.timesCount > 0) && (
            <Line
              type="monotone"
              dataKey="timesCount"
              stroke="#3b82f6"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-gray-900">
            {chartData.length}
          </p>
          <p className="text-sm text-gray-500">Total Revisiones</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-green-600">
            {chartData.filter(d => d.detected === 1).length}
          </p>
          <p className="text-sm text-gray-500">Citas Encontradas</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-primary-600">
            {(chartData.filter(d => d.detected === 1).length / chartData.length * 100).toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500">Tasa de Éxito</p>
        </div>
      </div>
    </div>
  )
}
